import { useRef, useState } from "react";
import type { Counterparty } from "@common/types/counterparty";
import type { CounterpartyTinIds } from "@api/counterpartyTin.api";

export type TinSaveFailure =
    | { status: "stepUpRequired" }
    | { status: "failed"; message: string | null };

export type TinSubmitResult = { status: "saved" } | TinSaveFailure;

export type TinEntryStatus = "empty" | "invalid" | "valid";

export interface TinEntryHandle {
    status: TinEntryStatus;
    submit: (ids: CounterpartyTinIds) => Promise<TinSubmitResult>;
}

export type TinDraftError = { status: "invalid" } | TinSaveFailure;

interface TinEntryChange {
    hasInput: boolean;
    isValid: boolean;
}

export function useTinDraft() {
    const entryRef = useRef<TinEntryHandle | null>(null);
    const [hasInput, setHasInput] = useState(false);
    const [error, setError] = useState<TinDraftError | null>(null);
    const [isReplacing, setIsReplacing] = useState(false);
    const lastChangeRef = useRef<TinEntryChange | null>(null);

    // VGS keeps the first callback it is given, so this may only call setters
    // and refs. It also fires on focus and blur, so errors clear only when the
    // entry itself changes.
    const changeEntry = (change: TinEntryChange) => {
        const lastChange = lastChangeRef.current;
        lastChangeRef.current = change;
        setHasInput(change.hasInput);
        if (
            lastChange?.hasInput === change.hasInput &&
            lastChange.isValid === change.isValid
        ) {
            return;
        }
        const isStillInvalid = change.hasInput && !change.isValid;
        setError((current) => {
            if (current?.status === "stepUpRequired") {
                return current;
            }
            if (current?.status === "invalid" && isStillInvalid) {
                return current;
            }
            return null;
        });
    };

    const startReplacing = () => setIsReplacing(true);

    const cancelReplacing = () => {
        setIsReplacing(false);
        setHasInput(false);
        setError(null);
    };

    const validate = (): boolean => {
        if (entryRef.current?.status !== "invalid") {
            return true;
        }
        setError({ status: "invalid" });
        return false;
    };

    const save = async (counterparty: Counterparty): Promise<boolean> => {
        const entry = entryRef.current;
        if (!entry || entry.status === "empty") {
            return true;
        }
        if (entry.status === "invalid") {
            setError({ status: "invalid" });
            return false;
        }
        if (!counterparty.workspaceId) {
            setError({ status: "failed", message: null });
            return false;
        }
        const result = await entry.submit({
            workspaceId: counterparty.workspaceId,
            counterpartyId: counterparty.id,
        });
        if (result.status !== "saved") {
            setError(result);
            return false;
        }
        setError(null);
        setHasInput(false);
        setIsReplacing(false);
        return true;
    };

    return {
        entryRef,
        hasInput,
        error,
        isReplacing,
        changeEntry,
        startReplacing,
        cancelReplacing,
        validate,
        save,
    };
}

export type TinDraft = ReturnType<typeof useTinDraft>;
